import { memo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import { useGetWebsitePersistentAppsQuery } from 'src/api_services/apps/query';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { IconStack } from 'src/components/IconStack/IconStack';
import { Text } from 'src/components/Text';
import { runtimeIcons } from 'src/pages/projects/SelectableRuntime';
import { Viewport } from 'src/containers/Viewport';
import { PersistentAppKind, Website } from 'src/orchd-client';
import { isWebsiteNormalOrStaging } from 'src/utils/isWebsiteNormal';

export interface Props {
  website: Website;
  // styling of the Manage/Launch control; the hero uses 'tertiary-small',
  // which renders plain text with the whole chip clicking through to the
  // workers page instead of a button
  buttonVariant?: 'secondary-tiny' | 'tertiary-small';
}

export const _WorkerProcessStatus = ({ website, buttonVariant = 'secondary-tiny' }: Props) => {
  const canUsePersistentApps = website.canUse?.persistentApps ?? false;
  const isNormalOrStaging = isWebsiteNormalOrStaging(website.kind);

  const { data: persistentApps = [], isLoading } = useGetWebsitePersistentAppsQuery({
    websiteId: website.id,
    enabled: canUsePersistentApps && isNormalOrStaging,
  });

  // render nothing when the website cannot have persistent apps, and while
  // the first fetch is in flight so the manage/launch states don't flash
  if (!canUsePersistentApps || !isNormalOrStaging || isLoading) {
    return null;
  }
  // IconStack takes concrete icon names, so skip any app without a runtime kind.
  // openclaw and n8n run on node.js, so the hero chip reports their runtime instead
  // of the brand icons the shared map gives them
  const runtimeIconNames = persistentApps.flatMap((app) => {
    if (!app.appKind) {
      return [];
    }
    const kind =
      app.appKind === PersistentAppKind.openclaw || app.appKind === PersistentAppKind.n8n
        ? PersistentAppKind.nodejs
        : app.appKind;
    return [runtimeIcons[kind]];
  });

  const isPlainLink = buttonVariant === 'tertiary-small';
  const configured = runtimeIconNames.length !== 0;
  // hero style: the configured state is a plain-text click-through; the
  // launch state always stays a button (in its tertiary styling)
  const plainLink = isPlainLink && configured;

  const buttonProps =
    buttonVariant === 'tertiary-small'
      ? ({ variant: 'tertiary', size: 'small' } as const)
      : ({ variant: 'secondary', size: 'tiny' } as const);

  const workersUrl = `/websites/${website.id}/workers`;
  const targetUrl = configured ? workersUrl : `${workersUrl}#new`;

  const manageOrLaunchLabel = configured ? (
    isPlainLink ? (
      <FormattedMessage id="websites.website_dashboard.worker_process_status.running" defaultMessage="Running" />
    ) : (
      <FormattedMessage id="websites.website_dashboard.github_deploy.manage" defaultMessage="Manage" />
    )
  ) : (
    <FormattedMessage id="websites.website_dashboard.github_deploy.launch" defaultMessage="Launch" />
  );

  const control = plainLink ? (
    <Text size="sm">{manageOrLaunchLabel}</Text>
  ) : (
    <Link to={targetUrl}>
      <Button {...buttonProps}>{manageOrLaunchLabel}</Button>
    </Link>
  );

  const stack = configured ? (
    <Box mr="sm">
      <IconStack icons={runtimeIconNames} borderColor="green" />
    </Box>
  ) : (
    <>
      <Link to={targetUrl}>
        <IconStack icons={['node js thin grey', 'laravel grey', 'php2 grey']} iconColor="grey" />
      </Link>
      <Viewport tabletUp>
        <Text color="grey" weight="light">
          +
        </Text>
      </Viewport>
    </>
  );

  const statusRow = (
    <Box d="row" a="center" w="auto" wrap="nowrap" gap={configured ? undefined : 'sm'}>
      {stack}
      <Viewport tabletUp>{control}</Viewport>
    </Box>
  );

  // in the plain-link (hero) style the whole chip is the anchor
  return plainLink ? <Link to={targetUrl}>{statusRow}</Link> : statusRow;
};

export const WorkerProcessStatus = memo(_WorkerProcessStatus);
